import { Injectable } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { Apollo } from 'apollo-angular';
import { catchError, map, Observable } from 'rxjs';
import { SKIP_AUTH } from '../../interceptors/auth.interceptor';
import { SKIP_ERROR_HANDLER } from '../../../infrastructure/apollo/create-apollo';
import {
  ConsentType,
  IChangePasswordPayload,
  ILegalTexts,
  IMissingConsentsDto,
  IPersonalDataResponse,
  IResetPasswordBeanPayload,
} from '../../../models';
import {
  changeEmailMutation,
  changePasswordMutation,
  deleteUserMutation,
  getLegalTextsQuery,
  getPersonalDataQuery,
  initChangeEmailMutation,
  initResetPasswordMutation,
  resetPasswordMutation,
} from '../../../queries';
import { IConsent } from '../../../models/account/consent.model';
import {
  getConsentQuery,
  getMissingConsentsQuery,
  registerUserActivityMutation,
  setConsentMutation,
  setConsentV2Mutation,
} from '../../../queries/account/consent.queries';

@Injectable()
/**
 * Service including functions related to the account tab
 */
export class AccountUserFacade {
  constructor(private apollo: Apollo) {}

  public deleteUser(): Observable<boolean> {
    return this.apollo
      .mutate<boolean>({
        mutation: deleteUserMutation,
      })
      .pipe(
        map(result => result && result.data),
        catchError((error: Error) => {
          console.error(error);
          throw new Error(error.message);
        })
      );
  }

  public initChangeEmail(payload: { email: string }): Observable<boolean> {
    return this.apollo
      .mutate<{ success: boolean }>({
        mutation: initChangeEmailMutation,
        variables: {
          payload,
        },
      })
      .pipe(
        map(response => {
          return response.data.success;
        })
      );
  }

  public changeEmail(payload: { token: string }): Observable<boolean> {
    return this.apollo
      .mutate<{ success: boolean }>({
        mutation: changeEmailMutation,
        variables: {
          payload,
        },
      })
      .pipe(
        map(response => {
          return response.data.success;
        }),
        catchError((error: Error) => {
          console.error(error);
          throw new Error(error.message);
        })
      );
  }

  public changePassword(payload: IChangePasswordPayload): Observable<boolean> {
    return this.apollo
      .mutate<boolean>({
        mutation: changePasswordMutation,
        variables: {
          payload,
        },
      })
      .pipe(
        map(response => {
          return response.data;
        }),
        catchError((error: Error) => {
          console.error(error);
          throw new Error(error.message);
        })
      );
  }

  public initResetPassword(email: string): Observable<boolean> {
    const payload = {
      email,
    };
    return this.apollo
      .mutate<boolean>({
        mutation: initResetPasswordMutation,
        variables: {
          payload,
        },
        context: { httpContext: new HttpContext().set(SKIP_AUTH, true) },
      })
      .pipe(
        map(response => {
          return response.data;
        }),
        catchError((error: Error) => {
          console.error(error);
          throw new Error(error.message);
        })
      );
  }

  public resetPassword(payload: IResetPasswordBeanPayload): Observable<boolean> {
    return this.apollo
      .mutate<boolean>({
        mutation: resetPasswordMutation,
        variables: {
          payload,
        },
        context: { httpContext: new HttpContext().set(SKIP_AUTH, true) },
      })
      .pipe(
        map(response => {
          return response.data;
        }),
        catchError((error: Error) => {
          console.error(error);
          throw new Error(error.message);
        })
      );
  }

  public getLegalTexts(): Observable<ILegalTexts> {
    return this.apollo
      .query<{ legalTexts: ILegalTexts }>({
        query: getLegalTextsQuery,
        errorPolicy: 'all',
        context: { httpContext: new HttpContext().set(SKIP_AUTH, true) },
      })
      .pipe(
        map(response => response?.data?.legalTexts),
        catchError((error: Error) => {
          console.error(error);
          throw new Error(error.message);
        })
      );
  }

  public getPersonalData(): Observable<IPersonalDataResponse> {
    return this.apollo
      .query<{ getPersonalData: IPersonalDataResponse }>({
        query: getPersonalDataQuery,
        errorPolicy: 'all',
      })
      .pipe(
        map(response => response?.data?.getPersonalData),
        catchError((error: Error) => {
          console.error(error);
          throw new Error(error.message);
        })
      );
  }

  public getConsent(consentType: ConsentType): Observable<IConsent> {
    return this.apollo
      .query<{ getConsent: IConsent }>({
        query: getConsentQuery,
        variables: {
          consentType,
        },
        errorPolicy: 'all',
      })
      .pipe(map(response => response?.data?.getConsent));
  }

  public setConsent(
    consentType: ConsentType,
    consentId: string,
    consentGiven?: boolean
  ): Observable<boolean> {
    return this.apollo
      .mutate<boolean>({
        mutation: setConsentMutation,
        variables: {
          consentType,
          consentId,
          consentGiven,
        },
      })
      .pipe(map(response => response.data));
  }

  public setConsentV2(legalTextId: string, consentGiven?: boolean): Observable<boolean> {
    return this.apollo
      .mutate<boolean>({
        mutation: setConsentV2Mutation,
        variables: {
          legalTextId,
          consentGiven,
        },
      })
      .pipe(
        map(response => {
          return response.data;
        }),
        catchError((error: Error) => {
          throw error;
        })
      );
  }

  public registerUserActivity(): Observable<boolean> {
    return this.apollo
      .mutate<{ registerUserActivity: boolean }>({
        mutation: registerUserActivityMutation,
        // Fired in the background, not by the user — failures must stay silent.
        context: { [SKIP_ERROR_HANDLER]: true },
      })
      .pipe(map(response => response.data?.registerUserActivity));
  }

  public getMissingConsents(): Observable<IMissingConsentsDto> {
    return this.apollo
      .query<{ getMissingConsents: IMissingConsentsDto }>({
        query: getMissingConsentsQuery,
        errorPolicy: 'all',
        fetchPolicy: 'network-only',
      })
      .pipe(
        map(response => {
          return response?.data?.getMissingConsents;
        }),
        catchError((error: Error) => {
          throw new Error(error.message);
        })
      );
  }
}
